<template>
  <div class="chat-bubble-wrapper">
    <!-- 悬浮客服小球 -->
    <div 
      class="chat-bubble" 
      :class="{ 
        'expanded': showTooltip,
        'recording': isRecording,
        'transcribing': isTranscribing
      }"
      @click="handleClick"
      @mouseenter="showTooltip = true"
      @mouseleave="showTooltip = false"
    >
      <!-- 未读消息徽章（可选） -->
      <div v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</div>
      
      <!-- 图标 -->
      <div class="bubble-icon">
        <span v-if="isRecording">🎤</span>
        <span v-else-if="isTranscribing">🔄</span>
        <span v-else>💬</span>
      </div>
      
      <!-- 悬停提示 -->
      <div v-if="showTooltip" class="bubble-tooltip">
        <p class="tooltip-text">生活大師智能客服</p>
        <p class="tooltip-subtext">24小時在線</p>
        <p class="tooltip-shortcut">語音：數字鍵 0、Backspace 或 麥克風鍵（按住說話、放開結束）| 點擊：開啟聊天</p>
      </div>

      <!-- 錄音指示器 -->
      <div v-if="isRecording" class="recording-indicator">
        <span class="recording-dot"></span>
      </div>
    </div>

    <!-- 脉冲动画环 -->
    <div class="pulse-ring"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { usePlaylistStore } from '../store/usePlaylistStore.js';

const showTooltip = ref(false);
const unreadCount = ref(0);
const isRecording = ref(false);
const isTranscribing = ref(false);

// 獲取 Pinia store
const playlistStore = usePlaylistStore();

// 語音錄音相關變數
let mediaRecorder = null;
let audioChunks = [];
let stream = null;
let recordingStartTime = null;
let recordingDurationInterval = null;
let microphoneInitialized = false;

// 視窗管理相關變數
let openedWindow = null;
let openedWindowUrl = null;

// YouTube iframe 管理
let youtubeIframes = [];
let originalIframeSrcs = [];

// 後端 API 配置（語音轉文字已移至後端）
// 使用 SnapTV 後端 API（語音轉文字是通用功能）
// 注意：必須使用 tv.dailylife.xin 域名，因為 Nginx 將該域名的 /api/ 路由轉發到 SnapTV 後端
const TRANSCRIBE_API_URL = 'https://tv.dailylife.xin/api/voice/transcribe/';

// 觸發鍵配置：支援數字鍵 0、Backspace 與麥克風鍵（按住說話）
// 部分藍牙語音遙控器無數字鍵，麥克風鍵多走 BLE Report ID 10/11，網頁收不到；
// 若遙控器將麥克風鍵映射為鍵盤鍵（如 F20），可在此加入對應 code/key。
const TRIGGER_DESCRIPTORS = [
  { code: 'Digit0', key: '0' },
  { code: 'Backspace', key: 'Backspace' },
  { code: 'F20', key: 'F20' },
];
const isTriggerKey = (e) =>
  TRIGGER_DESCRIPTORS.some((t) => e.code === t.code || e.key === t.key);

// 關鍵詞映射表
const commandMap = [
  {
    keywords: ['東森新聞', '東森', '51台', '冬蔘新聞'],
    url: 'https://tv.dailylife.xin/tv/dashboard/V1p33hqPrUk',
    name: '東森新聞',
    priority: 1
  },
  {
    keywords: ['TVBS', 'TVBS NEWS', 'TVB', 'TVBS新聞'],
    url: 'https://tv.dailylife.xin/tv/dashboard/m_dhMSvUCIc',
    name: 'TVBS新聞',
    priority: 1
  },
  {
    keywords: ['鏡新聞', '鏡', '靜心文'],
    url: 'https://tv.dailylife.xin/tv/dashboard/5n0y6b0Q25o',
    name: '鏡新聞',
    priority: 1
  },
  {
    keywords: ['中天新聞', '中天', '中天電視'],
    url: 'https://tv.dailylife.xin/tv/dashboard/vr3XyVCR4T0',
    name: '中天新聞',
    priority: 1
  },
  {
    keywords: ['民視新聞', '民視'],
    url: 'https://tv.dailylife.xin/tv/dashboard/ylYJSBUgaMA',
    name: '民視新聞',
    priority: 1
  },
  {
    keywords: ['非凡財經', '非凡', '股市'],
    url: 'https://tv.dailylife.xin/tv/dashboard/eA6Aczd3FZM',
    name: '非凡財經',
    priority: 1
  },
  {
    keywords: ['公共電視', '13頻道', '13台'],
    url: 'https://tv.dailylife.xin/tv/dashboard/C6gYqSHLRw4',
    name: '公共電視',
    priority: 1
  },
  {
    keywords: ['公視新聞', '公視', '公式'],
    url: 'https://tv.dailylife.xin/tv/dashboard/quwqlazU-c8',
    name: '公視新聞',
    priority: 1
  },
  {
    keywords: ['華視新聞', '華視', '52台'],
    url: 'https://tv.dailylife.xin/tv/dashboard/wM0g8EoUZ_E',
    name: '華視新聞',
    priority: 1
  },
  {
    keywords: ['東森財經新聞', '東森財經'],
    url: 'https://tv.dailylife.xin/tv/dashboard/1I2iq41Akmo',
    name: '東森財經新聞',
    priority: 1
  },
  {
    keywords: ['首頁', '主頁', '首頁面'],
    url: 'https://dailylife.xin/',
    name: '首頁',
    priority: 1
  },
  {
    keywords: ['日曆', '行程', 'Google日曆', '行事曆'],
    url: 'https://dailylife.xin/calendar',
    name: '日曆',
    priority: 1
  },
  {
    keywords: ['帳本', '記帳本', '財務', '記帳'],
    url: 'https://dailylife.xin/accountbook',
    name: '帳本',
    priority: 1
  },
  {
    keywords: ['筆記', '筆記本'],
    url: 'https://dailylife.xin/notebook',
    name: '筆記',
    priority: 1
  },
  {
    keywords: ['名片本', '電話本', '名片簿', '名片'],
    url: 'https://dailylife.xin/businesscard',
    name: '名片本',
    priority: 1
  },
  {
    keywords: ['穿搭', '試衣', '試穿', '四川', '試衣間'],
    url: 'https://tryon.dailylife.xin/outfit/',
    name: '穿搭',
    priority: 1
  },
  {
    keywords: ['卡啦ok', '唱歌', 'Ktv', 'k歌', 'kalaoke', '卡拉ok', '卡拉OK', 'Karaoke', 'karaoke', 'KARAOKE'],
    url: 'https://ordersong.dailylife.xin/',
    name: '卡啦ok',
    priority: 1
  },
  {
    keywords: ['熱唱歌曲-國語', '國語熱唱', '國語歌曲'],
    url: 'https://ordersong.dailylife.xin/featuredsongs?param1=C&param2=A',
    name: '熱唱歌曲-國語',
    priority: 1
  },
  {
    keywords: ['熱唱歌曲-台語', '台語熱唱', '台語歌曲'],
    url: 'https://ordersong.dailylife.xin/featuredsongs?param1=C&param2=B',
    name: '熱唱歌曲-台語',
    priority: 1
  },
  {
    keywords: ['每日新問', '每日新聞'],
    url: 'https://dailylife.xin/news/',
    name: '新聞',
    priority: 1
  },
  {
    keywords: ['電視', '直播電視', '網路新問', '網路新聞', '電視台'],
    url: 'https://tv.dailylife.xin/tv/dashboard',
    name: '電視',
    priority: 1
  },
  {
    keywords: ['翻譯旅遊', '旅遊翻譯', '理由翻譯', '翻譯菜單', '標示', '路標', '名片管理', '自動識別', '穿搭評鑑', '穿搭風格', '工具'],
    url: 'https://dailylife.xin/tools',
    name: '工具',
    priority: 1
  }
];

// API Key 已移至後端，無需在前端載入
// listAvailableModels 函數已移除（API Key 已移至後端）

// 智能匹配引擎
function matchCommand(text) {
  if (!text || !text.trim()) {
    return null;
  }

  const normalizedText = text.trim().toLowerCase();
  let bestMatch = null;
  let bestScore = 0;

  for (const command of commandMap) {
    for (const keyword of command.keywords) {
      const normalizedKeyword = keyword.toLowerCase();
      
      // 精確匹配
      if (normalizedText === normalizedKeyword) {
        return {
          command: command,
          matchType: 'exact',
          matchedKeyword: keyword,
          score: 100
        };
      }
      
      // 完全包含匹配
      if (normalizedText.includes(normalizedKeyword) || normalizedKeyword.includes(normalizedText)) {
        const score = (normalizedKeyword.length / normalizedText.length) * 80;
        if (score > bestScore) {
          bestScore = score;
          bestMatch = {
            command: command,
            matchType: 'contains',
            matchedKeyword: keyword,
            score: score
          };
        }
      }
      
      // 部分匹配
      if (normalizedText.includes(normalizedKeyword.substring(0, Math.min(3, normalizedKeyword.length)))) {
        const score = 50;
        if (score > bestScore && !bestMatch) {
          bestScore = score;
          bestMatch = {
            command: command,
            matchType: 'partial',
            matchedKeyword: keyword,
            score: score
          };
        }
      }
    }
  }

  return bestMatch;
}

// 處理點擊事件（保持原有邏輯）
const handleClick = () => {
  // 鼠標點擊維持原來的程式邏輯
  openChat('click');
};

// 打開聊天頁面
const openChat = (triggerSource = 'click') => {
  console.log(`💬 客服浮球被觸發，觸發方式: ${triggerSource}`);
  
  // 存儲觸發方式
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('chat_trigger_source', triggerSource);
    sessionStorage.setItem('chat_trigger_time', new Date().toISOString());
  }
  
  // 通過 URL 參數傳遞觸發方式
  const chatUrl = `https://dailylife.xin/chat?trigger=${triggerSource}`;
  const targetName = 'dailylife_main_window';
  
  // 先清除前一視窗，避免同時有兩個聲音
  if (openedWindow && !openedWindow.closed) {
    try {
      openedWindow.close();
      console.log('✅ 已關閉前一視窗，避免同時播放多個聲音');
    } catch (closeError) {
      console.log('⚠️ 無法關閉舊視窗（可能已被用戶關閉）');
    }
  }
  
  // 創建新視窗
  openedWindow = window.open(chatUrl, targetName);
  openedWindowUrl = chatUrl;
  console.log('🚀 打開新視窗：聊天頁面');
};

// 處理 keydown 事件（按住開始錄音）
const handleKeyDown = async (event) => {
  if (!isTriggerKey(event)) return;
  event.preventDefault();
  event.stopPropagation();

  if (!isRecording.value) {
    try {
      await startRecording();
    } catch (error) {
      console.error('❌ 開始錄音時發生錯誤:', error);
    }
  }
};

// 處理 keyup 事件（放開結束錄音）
const handleKeyUp = async (event) => {
  if (!isTriggerKey(event)) return;
  event.preventDefault();
  event.stopPropagation();

  if (isRecording.value) {
    await stopRecording();
  }
};

// 自訂事件：供原生／BLE 橋接使用（如偵測 VoHoGP Report ID 10 Start/Stop）
const VOICE_TRIGGER_EVENT = 'snapTV:voiceTrigger';
const handleVoiceTriggerEvent = async (e) => {
  const type = e.detail?.type;
  if (type === 'start' && !isRecording.value) {
    try {
      await startRecording();
    } catch (err) {
      console.error('❌ [voiceTrigger] 開始錄音失敗:', err);
    }
  } else if (type === 'stop' && isRecording.value) {
    await stopRecording();
  }
};

// 初始化麥克風
// 釋放麥克風資源
function releaseMicrophone() {
  if (stream) {
    console.log('🔌 正在釋放麥克風資源...');
    stream.getTracks().forEach(track => {
      track.stop();
      console.log(`✅ 已停止音頻軌道: ${track.kind} (${track.label})`);
    });
    stream = null;
    microphoneInitialized = false;
    console.log('✅ 麥克風資源已釋放');
  }
}

// 獲取麥克風（只在需要時獲取）
async function getMicrophone() {
  // 如果已經有有效的 stream，直接返回
  if (stream && stream.getTracks().some(track => track.readyState === 'live')) {
    console.log('✅ 使用現有的麥克風 stream');
    return stream;
  }
  
  // 釋放舊的 stream（如果存在）
  releaseMicrophone();
  
  try {
    console.log('🎤 正在獲取麥克風權限...');
    stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 16000
      } 
    });

    microphoneInitialized = true;
    console.log('✅ 麥克風權限已獲取');
    return stream;
  } catch (error) {
    console.error('❌ 獲取麥克風失敗:', error);
    microphoneInitialized = false;
    throw error;
  }
}

// 靜音所有 YouTube iframe（使用 postMessage，避免重新載入 iframe）
function muteYouTubeIframes() {
  youtubeIframes = [];
  
  // 找到所有 YouTube iframe（多種選擇器確保能找到）
  const iframes = document.querySelectorAll('iframe[src*="youtube"], iframe[src*="youtu.be"], iframe.youtube-iframe, iframe[class*="youtube"]');
  
  iframes.forEach((iframe, index) => {
    // 檢查 iframe 是否支持 enablejsapi=1（必須在 URL 中）
    const src = iframe.src || iframe.getAttribute('src') || '';
    if (!src.includes('enablejsapi=1')) {
      console.log(`⚠️ YouTube iframe #${index} 未啟用 enablejsapi=1，無法使用 postMessage 控制`);
      return;
    }
    
    youtubeIframes[index] = iframe;
    
    // 使用 postMessage 發送 mute 命令（不會重新載入 iframe，避免黑屏閃動）
    try {
      iframe.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
      console.log(`🔇 已靜音 YouTube iframe #${index} (使用 postMessage)`);
    } catch (error) {
      console.warn(`⚠️ 無法靜音 YouTube iframe #${index}:`, error);
    }
  });
  
  if (youtubeIframes.length > 0) {
    console.log(`🔇 已靜音 ${youtubeIframes.length} 個 YouTube 播放器`);
  } else {
    console.log('ℹ️ 未找到 YouTube 播放器');
  }
}

// 恢復 YouTube iframe 音量（使用 postMessage，避免重新載入 iframe）
function unmuteYouTubeIframes() {
  youtubeIframes.forEach((iframe, index) => {
    if (iframe) {
      try {
        // 使用 postMessage 發送 unMute 命令（不會重新載入 iframe，避免黑屏閃動）
        iframe.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
        console.log(`🔊 已恢復 YouTube iframe #${index} 音量 (使用 postMessage)`);
      } catch (error) {
        console.warn(`⚠️ 無法恢復 YouTube iframe #${index} 音量:`, error);
      }
    }
  });
  
  youtubeIframes = [];
}

// 開始錄音
async function startRecording() {
  if (isRecording.value) {
    console.warn('⚠️ 已經在錄音中');
    return;
  }

  try {
    // 靜音所有 YouTube 播放器
    muteYouTubeIframes();
    
    // 獲取麥克風（只在需要時獲取）
    await getMicrophone();

    // 檢查 MediaRecorder 支持的格式
    let mimeType = 'audio/webm;codecs=opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/mp4';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = '';
        }
      }
    }

    // 創建 MediaRecorder
    const options = mimeType ? { mimeType } : {};
    mediaRecorder = new MediaRecorder(stream, options);

    audioChunks = [];
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      await handleRecordingComplete();
    };

    mediaRecorder.start();
    isRecording.value = true;
    recordingStartTime = Date.now();
    
    console.log('🎤 開始錄音...');
    
  } catch (error) {
    console.error('❌ 開始錄音失敗:', error);
    isRecording.value = false;
    // 恢復 YouTube 播放器音量
    unmuteYouTubeIframes();
    // 釋放麥克風資源
    releaseMicrophone();
    alert('無法訪問麥克風: ' + error.message);
  }
}

// 停止錄音
async function stopRecording() {
  if (!isRecording.value || !mediaRecorder) {
    return;
  }

  mediaRecorder.stop();
  isRecording.value = false;
  
  // 恢復 YouTube 播放器音量
  unmuteYouTubeIframes();
  
  if (recordingDurationInterval) {
    clearInterval(recordingDurationInterval);
    recordingDurationInterval = null;
  }

  const duration = Math.floor((Date.now() - recordingStartTime) / 1000);
  console.log(`🛑 停止錄音，持續時間: ${duration}秒`);
  
  isTranscribing.value = true;
}

// 處理錄音完成
async function handleRecordingComplete() {
  if (audioChunks.length === 0) {
    console.warn('⚠️ 沒有錄音數據');
    isTranscribing.value = false;
    return;
  }

  // 合併音頻片段
  const audioBlob = new Blob(audioChunks, { 
    type: mediaRecorder.mimeType || 'audio/webm' 
  });

  const audioSize = audioBlob.size;
  console.log(`✅ 錄音完成，音頻大小: ${audioSize} bytes`);

  try {
    // 調用 Google Gemini API 轉錄
    const text = await transcribeAudio(audioBlob);
    
    if (text && text.trim()) {
      console.log(`💬 語音轉文字結果: ${text}`);
      
      // 智能匹配並自動打開
      const matchResult = matchCommand(text);
      
      if (matchResult) {
        // 匹配成功，自動打開對應功能
        console.log(`✅ 匹配成功：${matchResult.command.name}`);
        
        // 處理視窗切換
        const targetUrl = matchResult.command.url;
        const targetName = 'dailylife_main_window'; // 使用固定的視窗名稱
        
        setTimeout(() => {
          // 先清除前一視窗，避免同時有兩個聲音
          if (openedWindow && !openedWindow.closed) {
            try {
              openedWindow.close();
              console.log('✅ 已關閉前一視窗，避免同時播放多個聲音');
            } catch (closeError) {
              console.log('⚠️ 無法關閉舊視窗（可能已被用戶關閉）');
            }
          }
          
          // 創建新視窗
          openedWindow = window.open(targetUrl, targetName);
          openedWindowUrl = targetUrl;
          console.log(`🚀 打開新視窗：${matchResult.command.name} - ${targetUrl}`);
        }, 300);
      } else {
        // 匹配失敗，調用 YouTube 搜索並添加到待播清單（不打開新視窗，避免影響 playerhc.vue 和 testpinia.vue）
        console.log('⚠️ 無法匹配，調用 YouTube 搜索並添加到待播清單');
        
        // 調用 YouTube 搜索並將結果添加到待播清單
        await searchYouTubeAndAddToPlaylist(text);
      }
      
      // 存儲轉錄結果
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('chat_trigger_source', 'voice');
        sessionStorage.setItem('chat_voice_text', text);
        sessionStorage.setItem('chat_trigger_time', new Date().toISOString());
      }
    } else {
      console.warn('⚠️ 轉錄結果為空');
    }
  } catch (error) {
    console.error('❌ 轉錄失敗:', error);
    alert('語音轉文字失敗: ' + error.message);
  } finally {
    isTranscribing.value = false;
    // 確保恢復 YouTube 播放器音量
    unmuteYouTubeIframes();
    // 錄音完成後立即釋放麥克風資源，避免佔用系統資源
    releaseMicrophone();
  }
}

// YouTube 搜索並添加到待播清單函數（參考 Dashboard.vue 和 songsearch.vue 的邏輯）
const searchYouTubeAndAddToPlaylist = async (query, maxResults = 10) => {
  console.log('🔍 開始 YouTube 搜索並添加到待播清單，關鍵字:', query);
  
  if (!query || !query.trim()) {
    console.log('⚠️ 搜索關鍵字為空');
    return;
  }
  
  try {
    // 調用 SnapTV 的 YouTube API（參考 Dashboard.vue 的邏輯）
    const response = await fetch('https://tv.dailylife.xin/api/videos/youtube_search/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tags: [query], // 將搜索關鍵字作為標籤傳遞
        max_results: maxResults
      }),
      // 增加超時時間到 120 秒
      signal: AbortSignal.timeout(120000)
    });

    // 檢查響應狀態
    if (!response.ok) {
      if (response.status === 504) {
        const errorText = await response.text().catch(() => 'Gateway Timeout');
        console.error('❌ 504 Gateway Timeout - 後端處理超時');
        throw new Error('搜索請求超時（504），請稍後再試');
      }
      
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        const errorText = await response.text().catch(() => '');
        if (errorText && !errorText.startsWith('<')) {
          errorMessage = errorText;
        }
      }
      
      throw new Error(`搜索請求失敗: ${errorMessage}`);
    }

    // 嘗試解析 JSON
    let data;
    try {
      const responseText = await response.text();
      if (responseText.trim().startsWith('<')) {
        console.error('❌ 收到 HTML 響應而非 JSON:', responseText.substring(0, 200));
        throw new Error('後端返回了錯誤頁面而非 JSON 數據');
      }
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ JSON 解析失敗:', parseError);
      throw new Error('無法解析後端響應');
    }
    
    console.log('📡 YouTube API 響應:', data);
    
    if (data.success && data.videos && data.videos.length > 0) {
      console.log('✅ YouTube API 成功，找到', data.videos.length, '個視頻');
      
      // 將搜索結果的第一個視頻添加到待播清單（避免待播清單過長）
      const firstVideo = data.videos[0];
      const youtubeUrl = `https://www.youtube.com/watch?v=${firstVideo.id}`;
      
      playlistStore.addSong({
        id: firstVideo.id,
        songname: firstVideo.title,
        filename: youtubeUrl,
        stateA: false,
        stateB: false,
      });
      
      console.log(`✅ 已將 YouTube 視頻添加到待播清單: ${firstVideo.title}`);
      console.log(`📊 當前待播清單數量: ${playlistStore.playlist.length}`);
    } else {
      console.error('❌ YouTube API 搜索失敗:', data.error || '未知錯誤');
      alert('YouTube 搜索失敗，請檢查網絡連接');
    }
    
  } catch (error) {
    console.error('❌ YouTube 搜索請求失敗:', error);
    
    let errorMessage = error.message || '未知錯誤';
    if (error.name === 'AbortError' || errorMessage.includes('timeout') || errorMessage.includes('超時')) {
      errorMessage = '搜索請求超時（超過 120 秒），請稍後再試';
    } else if (errorMessage.includes('504')) {
      errorMessage = '服務器響應超時（504 Gateway Timeout），請稍後再試';
    }
    
    alert('YouTube 搜索失敗: ' + errorMessage);
  }
};

// 將 Blob 轉換為 Base64
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // 移除 data URL 前綴（data:audio/webm;base64,）
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// 調用後端 API 轉錄音頻（API Key 已移至後端）
async function transcribeAudio(audioBlob) {
  try {
    // 將音頻 blob 轉換為 base64
    const base64Audio = await blobToBase64(audioBlob);
    
    // 確定音頻的 MIME 類型
    const mimeType = audioBlob.type || 'audio/webm';
    
    console.log('🔄 正在調用後端語音轉文字 API...');
    console.log(`📊 音頻大小: ${(audioBlob.size / 1024).toFixed(2)} KB, MIME 類型: ${mimeType}`);
    console.log(`📡 API URL: ${TRANSCRIBE_API_URL}`);
    
    // 調用後端 API
    const response = await fetch(TRANSCRIBE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        audio_base64: base64Audio,
        mime_type: mimeType
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `後端 API 錯誤: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // 解析後端 API 響應
    if (data.success && data.text) {
      console.log('✅ 語音轉文字成功');
      return data.text.trim();
    } else {
      throw new Error(data.error || '無法從後端 API 獲取有效響應');
    }
  } catch (error) {
    console.error('❌ 語音轉文字 API 調用失敗:', error);
    throw error;
  }
}

// 初始化
onMounted(async () => {
  // API Key 已移至後端，無需載入

  // 添加鍵盤事件監聽器
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);

  // 防止頁面滾動（當按下任一觸發鍵時）
  document.addEventListener('keydown', (e) => {
    if (isTriggerKey(e)) e.preventDefault();
  });

  window.addEventListener(VOICE_TRIGGER_EVENT, handleVoiceTriggerEvent);

  console.log('✅ 麥克風將在需要時按需獲取');
  console.log('⌨️ 語音觸發：數字鍵 0、Backspace 或 F20（麥克風鍵）| 按住說話、放開結束');
});

// 清理資源
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('keyup', handleKeyUp);
  window.removeEventListener(VOICE_TRIGGER_EVENT, handleVoiceTriggerEvent);

  // 停止錄音
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }

  // 恢復 YouTube 播放器音量
  unmuteYouTubeIframes();

  // 停止音頻流（使用統一的釋放函數）
  releaseMicrophone();

  if (recordingDurationInterval) {
    clearInterval(recordingDurationInterval);
    recordingDurationInterval = null;
  }
});
</script>

<style scoped>
.chat-bubble-wrapper {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 9999;
}

.chat-bubble {
  position: relative;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  animation: float 3s ease-in-out infinite;
}

.chat-bubble:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.6);
}

.chat-bubble.expanded {
  width: 200px;
  border-radius: 30px;
}

.chat-bubble.recording {
  background: linear-gradient(135deg, #ff4444 0%, #ff6b6b 100%);
  animation: recording-pulse 1s ease-in-out infinite;
}

.chat-bubble.transcribing {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  animation: transcribing-spin 1s linear infinite;
}

@keyframes recording-pulse {
  0%, 100% {
    box-shadow: 0 4px 20px rgba(255, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(255, 68, 68, 0.8);
  }
}

@keyframes transcribing-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 浮动动画 */
@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* 脉冲环动画 */
.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border: 3px solid #667eea;
  border-radius: 50%;
  animation: pulse 2s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

/* 图标 */
.bubble-icon {
  font-size: 28px;
  transition: all 0.3s;
  animation: wobble 4s ease-in-out infinite;
}

@keyframes wobble {
  0%, 100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-10deg);
  }
  75% {
    transform: rotate(10deg);
  }
}

.chat-bubble:hover .bubble-icon {
  transform: scale(1.2);
}

/* 未读徽章 */
.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 22px;
  height: 22px;
  background: #ff4444;
  color: white;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  padding: 0 6px;
  box-shadow: 0 2px 8px rgba(255, 68, 68, 0.6);
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

/* 悬停提示 */
.bubble-tooltip {
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  animation: slideIn 0.3s;
  pointer-events: none;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
}

.bubble-tooltip::after {
  content: '';
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid white;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
}

.tooltip-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.tooltip-subtext {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #666;
}

.tooltip-shortcut {
  margin: 4px 0 0 0;
  font-size: 10px;
  color: #999;
}

/* 錄音指示器 */
.recording-indicator {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 16px;
  height: 16px;
  background: #ff4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.recording-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .chat-bubble-wrapper {
    bottom: 20px;
    right: 20px;
  }
  
  .chat-bubble {
    width: 56px;
    height: 56px;
  }
  
  .bubble-icon {
    font-size: 26px;
  }
  
  .pulse-ring {
    width: 56px;
    height: 56px;
  }
  
  .bubble-tooltip {
    right: 65px;
    padding: 10px 14px;
  }
  
  .tooltip-text {
    font-size: 13px;
  }
  
  .tooltip-subtext {
    font-size: 11px;
  }
}

/* 小手机屏幕 */
@media (max-width: 480px) {
  .chat-bubble-wrapper {
    bottom: 15px;
    right: 15px;
  }
  
  .chat-bubble {
    width: 52px;
    height: 52px;
  }
  
  .bubble-icon {
    font-size: 24px;
  }
  
  .bubble-tooltip {
    display: none; /* 小屏幕不显示悬停提示 */
  }
}
</style>
